import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import AuthImage from './AuthImage';

export default function AdminUploads({ user, embedded = false }) {
    const [data, setData]         = useState(null);
    const [loading, setLoading]   = useState(true);
    const [selected, setSelected] = useState(new Set());
    const [busy, setBusy]         = useState(false);
    const [tab, setTab]           = useState('flagged'); // 'flagged' | 'expired'

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/admin_uploads.php?action=list`);
            const json = await res.json();
            if (json.success) setData(json);
        } catch {}
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const rows = tab === 'flagged' ? (data?.flagged ?? []) : (data?.expired ?? []);

    const toggle = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selected.size === rows.length) setSelected(new Set());
        else setSelected(new Set(rows.map(r => r.id)));
    };

    const deleteSelected = async () => {
        if (!selected.size || !window.confirm(`Supprimer ${selected.size} image(s) ?`)) return;
        setBusy(true);
        await apiClient(`${process.env.REACT_APP_API_BASE_URL}/admin_uploads.php?action=delete`, {
            method: 'POST',
            body: JSON.stringify({ ids: [...selected] }),
        });
        setSelected(new Set());
        await load();
        setBusy(false);
    };

    const cleanupExpired = async () => {
        if (!window.confirm(`Supprimer toutes les images expirées (> 30 jours) ?`)) return;
        setBusy(true);
        await apiClient(`${process.env.REACT_APP_API_BASE_URL}/admin_uploads.php?action=cleanup_expired`, { method: 'POST' });
        await load();
        setBusy(false);
    };

    if (user?.role !== 'admin') {
        return <div className="flex items-center justify-center h-screen text-gray-500">Accès réservé aux administrateurs.</div>;
    }

    return (
        <div className={embedded ? '' : 'min-h-screen bg-gray-50 dark:bg-gray-950 p-6'}>
            <div className={embedded ? '' : 'max-w-5xl mx-auto'}>
                {!embedded && <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Gestion des images</h1>}

                {loading ? (
                    <div className="text-gray-500 text-center py-20">Chargement…</div>
                ) : (
                    <>
                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-4 mb-6">
                            {[
                                { label: 'Total', value: data?.stats?.total ?? 0, color: 'blue' },
                                { label: 'Signalées', value: data?.stats?.flagged_count ?? 0, color: 'red' },
                                { label: 'Expirées', value: data?.stats?.expired_count ?? 0, color: 'yellow' },
                            ].map(({ label, value, color }) => (
                                <div key={label} className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-white/10 text-center">
                                    <div className={`text-3xl font-bold text-${color}-600 dark:text-${color}-400`}>{value}</div>
                                    <div className="text-sm text-gray-500 mt-1">{label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-2 mb-4">
                            {['flagged', 'expired'].map(t => (
                                <button key={t} onClick={() => { setTab(t); setSelected(new Set()); }}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t ? 'bg-cyan-500 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-white/10 hover:border-cyan-400'}`}>
                                    {t === 'flagged' ? `Signalées (${data?.flagged?.length ?? 0})` : `Expirées (${data?.expired?.length ?? 0})`}
                                </button>
                            ))}
                            {tab === 'expired' && (data?.expired?.length ?? 0) > 0 && (
                                <button onClick={cleanupExpired} disabled={busy}
                                    className="ml-auto px-4 py-2 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors">
                                    Tout nettoyer
                                </button>
                            )}
                            {selected.size > 0 && (
                                <button onClick={deleteSelected} disabled={busy}
                                    className="ml-auto px-4 py-2 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors">
                                    Supprimer ({selected.size})
                                </button>
                            )}
                        </div>

                        {/* Table */}
                        {rows.length === 0 ? (
                            <div className="text-center py-16 text-gray-400">Aucune image dans cette catégorie.</div>
                        ) : (
                            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden overflow-x-auto">
                                <table className="w-full text-sm min-w-[500px]">
                                    <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 text-xs uppercase">
                                        <tr>
                                            <th className="p-3 w-8"><input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleAll} /></th>
                                            <th className="p-3 text-left">Aperçu</th>
                                            <th className="p-3 text-left">Élève</th>
                                            {tab === 'flagged' && <th className="p-3 text-left">Signalement</th>}
                                            <th className="p-3 text-left">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                                        {rows.map(row => (
                                            <tr key={row.id} className={`hover:bg-gray-50 dark:hover:bg-white/5 ${selected.has(row.id) ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                                                <td className="p-3"><input type="checkbox" checked={selected.has(row.id)} onChange={() => toggle(row.id)} /></td>
                                                <td className="p-3">
                                                    <AuthImage uploadId={row.id} className="h-14 w-14 object-cover rounded-lg border border-gray-200 dark:border-white/10" />
                                                </td>
                                                <td className="p-3 text-gray-700 dark:text-gray-300">{row.student_name ?? '—'}</td>
                                                {tab === 'flagged' && (
                                                    <td className="p-3">
                                                        <span className="text-red-600 dark:text-red-400 font-medium">{row.flag_reason}</span>
                                                        <div className="text-xs text-gray-400">{row.flagged_at?.substring(0, 16)}</div>
                                                    </td>
                                                )}
                                                <td className="p-3 text-gray-400 text-xs">{row.created_at?.substring(0, 16)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
